/**
 * 仿制图章 / 修复画笔（M3-12 / DESIGN.md §6.2，S / Y 键）。
 *
 * Alt+点击设采样点；落笔时确立源偏移（对齐模式：offset = 采样点 − 起笔点，
 * 此后所有笔画共用同一偏移，源随笔画同步位移——PS 的默认行为）。
 * 覆盖度管线复用画笔（采样→stamp→StrokeBuffer）。
 *
 * 两个工具共用这一个控制器——交互（Alt+点击定源、拖拽对齐平移）完全一样，
 * 区别只在收笔时走哪个 merge：仿制图章直接克隆（mergeCloneInto），
 * 修复画笔额外做一次色彩匹配（mergeHealingCloneInto，见 buffer.ts 的
 * 文档注释——不是真的泊松融合，是均色偏移的近似）。
 *
 * 取样点/取样源指示（跟真实 Photoshop 一致）：设完采样点之前光标下什么
 * 提示都没有会让人怀疑"到底有没有生效"——`drawOverlay` 画一个黑白十字：
 * 还没画过笔（`#offset` 是 null）时钉在采样点本身；已经确立对齐偏移后
 * 跟着指针实时移动、显示"此刻会从哪里取色"，跟画笔笔尖同步。
 *
 * 实时预览（跟真实 Photoshop 一致）：笔画期间只往 `StrokeBuffer` 里攒
 * 覆盖度，真正写进 TileStore 是收笔（`#commit`）才做的事——这是画笔/
 * 修复画笔共用的既有纪律（M2-09：flow 在同一笔画内累积，收笔才落盘，
 * 见 buffer.ts 文件头），本身没有问题，但仿制图章缺了这套纪律配套的
 * "实时预览"那一半：画笔直接往同一图层画所以肉眼就能看见进度，仿制图章
 * 不能直接写图层（源可能还没冻结），不补一个预览就会像"划过去什么都
 * 没发生，松手才突然出现"——这里现算一份实时预览（`#preview` 离屏画布，
 * 复用跟 `mergeCloneInto` 一致的取样/合成公式，只是不写库、只画叠加层），
 * 每帧都直接从当前（还没被这一笔改动过的）图层取源/目标像素，笔画途中
 * 跟收笔时看到的结果应该逐像素一致。健康画笔的色彩匹配那一步（收笔时
 * 才算的整笔均色偏移）没法增量预览，实时预览这里对两个工具都只显示
 * "纯克隆"效果，健康画笔真正的色彩匹配只在松手那一刻才会补上——这是
 * 有意的简化，不是 bug。
 */
import { StrokeSampler } from '../../engine/brush/sampler';
import { StrokeBuffer } from '../../engine/brush/buffer';
import { TILE, tileKey, type LayerId } from '../../engine/tile/key';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';
import type { ToolSample } from './brush-tool';

/** 读一个图层当前（未提交）的像素——实时预览要跟 mergeCloneInto 同一个取样口径。 */
function readPixel(
  store: AppStore,
  layer: LayerId,
  x: number,
  y: number,
): readonly [number, number, number, number] {
  const doc = store.document;
  // 取整兜底：TypedArray 不支持非整数下标，读到 undefined 会在上层的
  // alpha 计算里变成 NaN（同 buffer.ts sourceAt() 的教训）
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  if (doc === null || ix < 0 || iy < 0 || ix >= doc.size.width || iy >= doc.size.height) {
    return [0, 0, 0, 0];
  }
  const tile = store.tiles.get(tileKey(layer, Math.floor(ix / TILE), Math.floor(iy / TILE)));
  const data = tile?.data;
  if (data == null) return [0, 0, 0, 0];
  const at = ((iy % TILE) * TILE + (ix % TILE)) * 4;
  return [data[at]!, data[at + 1]!, data[at + 2]!, data[at + 3]!];
}

export interface StampDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
}

export class StampToolController {
  readonly #deps: StampDeps;
  #sampler: StrokeSampler | null = null;
  #buffer: StrokeBuffer | null = null;
  #layer: LayerId | null = null;
  #opacity = 1;
  #tool: 'stamp' | 'healingBrush' = 'stamp';
  /** 采样点（文档空间）。Alt+点击设置。 */
  #sample: { x: number; y: number } | null = null;
  /** 对齐偏移：源 = 目标 + offset。第一笔落笔时确立。 */
  #offset: { x: number; y: number } | null = null;
  /** 指针当前位置（文档空间）——不管是不是正在画，光标一动就更新，供取样源指示跟手。 */
  #cursor: { x: number; y: number } | null = null;
  /** 实时预览离屏画布——笔画期间现算的克隆结果，收笔（`#commit`）后清空。 */
  #preview: HTMLCanvasElement | null = null;

  constructor(deps: StampDeps) {
    this.#deps = deps;
  }

  get samplePoint(): { x: number; y: number } | null {
    return this.#sample;
  }

  /** 指针处理。返回 true 表示已消费。 */
  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    modifiers: { altKey: boolean },
    samples: readonly ToolSample[],
  ): boolean {
    const tool = this.#deps.tools.active.id;
    if (tool !== 'stamp' && tool !== 'healingBrush') return false;
    const store = this.#deps.store;
    const doc = store.document;
    if (doc === null || samples.length === 0) return true;

    // 不管是不是正在画（悬停也算），光标一动就记下来，取样源指示才能跟手
    const last = samples[samples.length - 1]!;
    this.#cursor = { x: last.x, y: last.y };

    if (phase === 'down') {
      if (modifiers.altKey) {
        // 设新采样点并重置对齐（PS：重新取样后下一笔重新确立偏移）
        const p = samples[samples.length - 1]!;
        this.#sample = { x: p.x, y: p.y };
        this.#offset = null;
        store.setHint(tool === 'stamp' ? '已设置仿制源' : '已设置修复源');
        return true;
      }
      if (this.#sample === null) {
        store.setHint(tool === 'stamp' ? '先按住 Alt 点击设置仿制源' : '先按住 Alt 点击设置修复源');
        return true;
      }
      const layer = store.activeRasterLayerId();
      if (layer === null) return true;
      const start = samples[0]!;
      // 取整：非 100% 缩放下文档坐标本身就带小数，偏移量若也是小数，
      // 合并时 sourceAt() 按小数下标读 TileStore 会静默拿到 undefined
      // （TypedArray 不支持非整数下标），alpha 算出 NaN、写回时被强转成
      // 0——涂完整片变透明，颜色却原样保留，跟橡皮擦一模一样。仿制源偏移
      // 概念上就该是整像素位移，这里取整既修正了 bug 也更贴合语义。
      this.#offset ??= {
        x: Math.round(this.#sample.x - start.x),
        y: Math.round(this.#sample.y - start.y),
      };
      this.#layer = layer;
      this.#tool = tool;
      const size = this.#deps.tools.getOption(tool, 'size') as number;
      const hardness = (this.#deps.tools.getOption(tool, 'hardness') as number) / 100;
      this.#opacity = tool === 'stamp' ? (this.#deps.tools.getOption('stamp', 'opacity') as number) / 100 : 1;
      this.#sampler = new StrokeSampler({
        size,
        flow: 1,
        spacing: 0.25,
        smoothing: 0.5,
        pressureSize: false,
        pressureFlow: false,
      });
      this.#buffer = new StrokeBuffer(doc.size.width, doc.size.height, hardness);
      if (typeof document !== 'undefined') {
        this.#preview ??= document.createElement('canvas');
        this.#preview.width = doc.size.width;
        this.#preview.height = doc.size.height;
        this.#preview.getContext('2d')?.clearRect(0, 0, doc.size.width, doc.size.height);
      }
      this.#feed(samples);
      return true;
    }
    if (phase === 'move' && this.#sampler !== null) {
      this.#feed(samples);
      return true;
    }
    if (phase === 'up' && this.#sampler !== null) {
      this.#feed(samples);
      this.#commit();
      return true;
    }
    // 悬停（没有正在画的笔画）：只是记了 #cursor，得请求重绘一下取样源
    // 指示才会跟着挪，不然要等到下次真正落笔才刷新，看着像"卡住不动"
    if (phase === 'move' && this.#sample !== null) this.#deps.requestRender();
    return true;
  }

  /**
   * 文档空间叠加层：取样源指示，黑白十字（同一套"黑白各画一遍保证任何
   * 背景色下总有一半能看见"的手法，渐变工具的拖拽预览线也用这套）。
   * 没设过采样点（`#sample` 为 null）什么都不画；工具已经切到别的地方
   * 也不画——不然切走之后还留着一个不会动的十字很奇怪。
   */
  drawOverlay(ctx: CanvasRenderingContext2D): void {
    const tool = this.#deps.tools.active.id;
    if ((tool !== 'stamp' && tool !== 'healingBrush') || this.#sample === null) return;

    // 实时预览：笔画期间现算的克隆结果先画在这，画在十字标记下面——
    // 收笔清空之后这里就不会再画任何东西，不会跟真正落盘的像素重叠打架
    if (this.#preview !== null) ctx.drawImage(this.#preview, 0, 0);

    // 还没画过笔：钉在采样点本身；已经确立对齐偏移：跟着指针实时显示
    // "此刻会从哪取色"（源 = 光标位置 + offset，跟 #commit 里的公式一致）
    const point =
      this.#offset !== null && this.#cursor !== null
        ? { x: this.#cursor.x + this.#offset.x, y: this.#cursor.y + this.#offset.y }
        : this.#sample;
    const size = (this.#deps.tools.getOption(tool, 'size') as number | undefined) ?? 30;
    const arm = Math.max(8, size / 4);

    ctx.save();
    ctx.lineWidth = 1;
    for (const color of ['black', 'white'] as const) {
      ctx.strokeStyle = color;
      ctx.setLineDash(color === 'black' ? [] : [3, 3]);
      ctx.beginPath();
      ctx.moveTo(point.x - arm, point.y);
      ctx.lineTo(point.x + arm, point.y);
      ctx.moveTo(point.x, point.y - arm);
      ctx.lineTo(point.x, point.y + arm);
      ctx.stroke();
    }
    ctx.restore();
  }

  #feed(samples: readonly ToolSample[]): void {
    const sampler = this.#sampler;
    const buffer = this.#buffer;
    if (sampler === null || buffer === null) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let touched = false;
    for (const sample of samples) {
      for (const stamp of sampler.add(sample)) {
        buffer.stamp(stamp);
        touched = true;
        const r = stamp.size / 2;
        minX = Math.min(minX, stamp.x - r);
        minY = Math.min(minY, stamp.y - r);
        maxX = Math.max(maxX, stamp.x + r);
        maxY = Math.max(maxY, stamp.y + r);
      }
    }
    if (touched) this.#updatePreview(minX, minY, maxX, maxY);
  }

  /**
   * 实时预览：只算这一次新落的笔尖影响到的包围盒，跟 `mergeCloneInto` 同一套
   * 取样/合成公式，但只写进离屏画布、不写 TileStore——每帧现读现算，笔画
   * 途中看到的结果理应跟真正收笔时逐像素一致（健康画笔的色彩匹配那一步
   * 除外，见文件头注释）。
   */
  #updatePreview(minX: number, minY: number, maxX: number, maxY: number): void {
    const buffer = this.#buffer;
    const layer = this.#layer;
    const offset = this.#offset;
    const preview = this.#preview;
    const store = this.#deps.store;
    const doc = store.document;
    if (buffer === null || layer === null || offset === null || preview === null || doc === null) return;
    const ctx = preview.getContext('2d');
    if (ctx === null) return;

    const x0 = Math.max(0, Math.floor(minX));
    const y0 = Math.max(0, Math.floor(minY));
    const x1 = Math.min(doc.size.width, Math.ceil(maxX));
    const y1 = Math.min(doc.size.height, Math.ceil(maxY));
    if (x0 >= x1 || y0 >= y1) return;

    const selection = store.selection;
    const region = ctx.createImageData(x1 - x0, y1 - y0);
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) {
        const coverage = buffer.alphaAt(x, y);
        if (coverage <= 0) continue;
        const [sr, sg, sb, sa] = readPixel(store, layer, x + offset.x, y + offset.y);
        let alpha = coverage * this.#opacity * (sa / 255);
        if (selection !== null) alpha = (alpha * selection.valueAt(x, y)) / 255;
        if (alpha <= 0) continue;
        const [dr, dg, db, da0] = readPixel(store, layer, x, y);
        const dstA = da0 / 255;
        const outA = alpha + dstA * (1 - alpha);
        const at = ((y - y0) * (x1 - x0) + (x - x0)) * 4;
        if (outA > 0) {
          region.data[at] = Math.round((sr * alpha + dr * dstA * (1 - alpha)) / outA);
          region.data[at + 1] = Math.round((sg * alpha + dg * dstA * (1 - alpha)) / outA);
          region.data[at + 2] = Math.round((sb * alpha + db * dstA * (1 - alpha)) / outA);
        }
        region.data[at + 3] = Math.round(outA * 255);
      }
    }
    ctx.putImageData(region, x0, y0);
    this.#deps.requestRender();
  }

  #commit(): void {
    const sampler = this.#sampler;
    const buffer = this.#buffer;
    const layer = this.#layer;
    const offset = this.#offset;
    const store = this.#deps.store;
    this.#sampler = null;
    this.#buffer = null;
    if (sampler === null || buffer === null || layer === null || offset === null) return;

    for (const stamp of sampler.finish()) buffer.stamp(stamp);
    const selection = store.selection;
    if (selection !== null) buffer.clipToMask(selection);

    const label = this.#tool === 'stamp' ? '仿制图章' : '修复画笔';
    const transaction = store.beginPixelTransaction(label);
    if (this.#tool === 'stamp') {
      buffer.mergeCloneInto(store.tiles, layer, offset.x, offset.y, this.#opacity, transaction);
    } else {
      buffer.mergeHealingCloneInto(store.tiles, layer, offset.x, offset.y, this.#opacity, transaction);
    }
    store.commitPixels(label, transaction.end());
    store.invalidatePixels([...buffer.tileCoords()]);
    // 真正的像素已经落盘：清掉实时预览，不然叠加层会在真实内容上再画一份
    // "看起来一样但其实是旧公式算出来的"重影（修复画笔尤其如此——预览只是
    // 纯克隆近似，真正的色彩匹配收笔才补上，两者不该同时叠着显示）
    const doc = store.document;
    if (doc !== null) this.#preview?.getContext('2d')?.clearRect(0, 0, doc.size.width, doc.size.height);
    this.#deps.requestRender();
  }
}
